import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { UserChatbotResponse } from '@/features/auth/types';
import { userChatbotsApi } from '../api/userChatbotsApi';

export function UserChatbotsPage() {
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState<UserChatbotResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadChatbots = async () => {
      try {
        const data = await userChatbotsApi.listMine();
        if (data.length === 1) {
          navigate(`/chatbot/${data[0].id}`, { replace: true });
          return;
        }
        setChatbots(data);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'No se pudieron cargar tus chatbots');
      } finally {
        setLoading(false);
      }
    };

    loadChatbots();
  }, [navigate]);

  if (loading) {
    return <div className='container mx-auto py-6'>Cargando tus chatbots...</div>;
  }

  return (
    <div className='container mx-auto py-6 space-y-4'>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>Mis Chatbots</h1>
        <p className='text-muted-foreground'>Elegí el asistente con el que querés conversar.</p>
      </div>

      {chatbots.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No tenés chatbots disponibles</CardTitle>
            <CardDescription>
              Tu usuario pertenece a un tenant sin instancias de chatbot activas.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
          {chatbots.map(chatbot => (
            <Card key={chatbot.id}>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <MessageSquare className='h-5 w-5 text-primary' />
                  {chatbot.name}
                </CardTitle>
                <CardDescription>{chatbot.description || 'Chatbot disponible'}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  {chatbot.botsCount} bot{chatbot.botsCount === 1 ? '' : 's'} disponible
                  {chatbot.botsCount === 1 ? '' : 's'}
                </p>
                <Button className='w-full' onClick={() => navigate(`/chatbot/${chatbot.id}`)}>
                  Abrir chat
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
